import React, { useCallback } from 'react'
import { Icon, Label } from 'semantic-ui-react'
import './PlanetDetailsDisplay.scss'
import { GameBoard, PlanetDeed, OwnedPlanet, StarbaseLoan, NonDeed, Sheet } from '@prometheus/types'
import usePlayerActions from '../../hooks/usePlayerActions'
import { PlanetDetailsDisplayDeed } from './PlanetDetailsDisplayDeed'
import { TileUpgradesPopup } from './TileUpgradesPopup'
import { PlanetCyclePopup } from './PlanetCyclePopup'
import { useSelector } from '../../../../hooks/useSelector'
import classNames from 'classnames'
import { findOwner } from '../../../../utils/findOwner'
import { PlanetDetailsDisplayLoan } from './PlanetDetailsDisplayLoan'
import { useDispatch } from 'react-redux'
import { pushError } from '../../../../reducers/game/game'
import { isPlanetHoldingForUpgrade, truncateCash, willAttemptUpgradeNextYear, yearsUntilNextYield } from '@prometheus/utils'
import { getNextTileUpgrade } from './compactPlanet'
import { round } from 'lodash'
import { useGameBoardActions } from '../../hooks/useGameBoardActions'
import PromiseButtonV2 from '@/components/PromiseButtonV2'
import { Box, ButtonProps, Card, Cards, Divider, Popup, Stack, Typography } from '@prometheus/components'
import translateOrbitingPlanetColorRaw from '@/utils/translateOrbitingPlanetColorRaw'

interface PlanetDetailsDisplayProps extends React.HTMLAttributes<HTMLDivElement> {
  planet: keyof GameBoard
  style?: React.CSSProperties
  choice?: boolean
  rift?: boolean
  deed?: PlanetDeed | StarbaseLoan | NonDeed
  isPurchase?: boolean
  owned?: boolean
  ownedPlanet?: OwnedPlanet
  fullscreen?: boolean
}

export const PlanetDetailsDisplay = ({ planet, style, choice, rift, deed, isPurchase, owned, ownedPlanet, fullscreen }: PlanetDetailsDisplayProps) => {
  const {
    onPickChoosablePlanet,
    onUpgradePlanet,
  } = usePlayerActions()
  const {
    focusPlanet,
  } = useGameBoardActions()

  const tile = useSelector(s => s.game.sheet?.planets[planet]?.tile) || 0
  const turn = useSelector(s => s.game.metadata?.turn) || 0
  const players_choosing_planet = useSelector(s => s.game.metadata?.players_choosing_planet) || []
  const players = useSelector(s => s.game.metadata?.players)
  const uid = useSelector(s => s.auth.uid)
  const tile_upgrades = useSelector(s => s.game.metadata?.tile_upgrades)
  const disable_planet_tokens = useSelector(s => s.game.options?.disable_planet_tokens)
  const sublevels_current = useSelector(s => s.game.sheet?.planets[planet]?.sub_levels_current) || 0
  const upgrade_hold_turns = useSelector(s => s.game.sheet?.planets[planet]?.upgrade_hold_turns) || 0
  const gameboard = useSelector(s => s.game.game_board)
  const sheet = useSelector(s => s.game.sheet) as Sheet || {}
  const dispatch = useDispatch()

  const onFocusPlanet = useCallback((planet) => {
    focusPlanet(planet)
  }, [focusPlanet])

  if (!players || !uid || !tile_upgrades) {
    return null
  }

  const owner = findOwner(planet, players)

  const isDeed = deed && deed.type === 'deed'
  const isLoan = deed && deed.type === 'starbase'
  const nextTileUpgrade = owned ? getNextTileUpgrade(tile, tile_upgrades, !!disable_planet_tokens) : { label: '', available: false }
  const next_resource = nextTileUpgrade.label
  const resource_qty_required = nextTileUpgrade.quantity

  // The engine adds the year's progress and attempts the upgrade in the SAME
  // tick, so from the state a player ever sees (a post-tick broadcast) the
  // attempt lands next turn exactly when `sub + 1 >= tile` — which is what
  // `willAttemptUpgradeNextYear` tests. Testing the engine's mid-tick rule
  // (`sub >= tile`) here only ever went true on a planet already holding,
  // which silenced the "about to upgrade, stock up now" warning entirely.
  const cycleState = { sub_levels_current: sublevels_current, tile, upgrade_hold_turns }
  // Finished its cycle but could not pay: it retries every year and holds its
  // progress meanwhile. Without this the planet looks simply broken.
  const isWaitingForResources = !!owned && isPlanetHoldingForUpgrade(cycleState)
  const isLevelingUp = !!owned && (isWaitingForResources || willAttemptUpgradeNextYear(cycleState))
  const yieldInYears = owned
    ? yearsUntilNextYield(cycleState, !!disable_planet_tokens)
    : null

  // `button` covers the actions for a planet you do NOT own (buy, travel,
  // warp). An owned planet's card shows two separate controls instead — the
  // cycle readout and the tile upgrade — built further down.
  let buttonText: any = false
  let disabled = false
  if (owned) {
    buttonText = false
  } else if (isPurchase) {
    if (isLoan) {
      buttonText = false
    } else if (owner === uid) {
      buttonText = 'Already Owned by You'
      disabled = true
    } else if (owner) {
      buttonText = `Owned by ${players[owner].name}`
      disabled = true
    } else {
      buttonText = 'Buy Planet'
    }
  } else if (choice && !rift) {
    if ((turn % 10 === 0) && (turn > 19)) {
      disabled = players_choosing_planet[0] !== uid || Object.values(players).some(p => p.current_planet === planet)
    }

    if (deed && (deed as any).hasOwnProperty('rent') && owner !== uid) {
      buttonText = `Travel (Rent: ${truncateCash((deed as any).rent)})`
    } else if (deed && (deed as any).hasOwnProperty('rent') && owner === uid) {
      buttonText = 'Travel (Owned by you)'
    } else {
      buttonText = 'Travel'
    }
  } else if (choice && rift) {
    if (deed && (deed as any).hasOwnProperty('rent') && owner !== uid) {
      buttonText = `Warp (Rent: ${truncateCash((deed as any).rent)})`
    } else if (deed && (deed as any).hasOwnProperty('rent') && owner === uid) {
      buttonText = 'Warp (Owned by you)'
    } else {
      buttonText = 'Warp'
    }
  }

  const orbit = planet.split(':')[0].split('|')[0]

  const connections = players_choosing_planet.length && gameboard?.[planet]?.connected_planets?.length && !owned ? (
    <Box space="sm">
      <Stack space="sm">
        <Typography retro variant="h4" style={{ lineHeight: 1 }} color="primary">CONNECTIONS</Typography>
        <Stack space="xs" direction="row" style={{ flexWrap: 'wrap' }}>
          {gameboard?.[planet]?.connected_planets?.map((p) => (
            <Label color="grey" style={{ margin: 0 }}>{p}</Label>
          ))}
        </Stack>
      </Stack>
    </Box>
  ) : (
    undefined
  )

  // Determine button color based on planet state
  let buttonColor: string
  if (disabled) {
    buttonColor = translateOrbitingPlanetColorRaw(orbit)
  } else if (owned && !next_resource) {
    buttonColor = 'var(--prometheus-dark)'
  } else if (owned) {
    // A cash rung has no quantity; the comparison used to be against
    // `undefined` and so was always false. Keep that, explicitly.
    const hasRequiredResources = resource_qty_required !== undefined
      && sheet.resources[next_resource] >= resource_qty_required
    const isCashUpgrade = next_resource === (tile_upgrades[0].cost.cash?.toString()) || 0
    
    if (hasRequiredResources || isCashUpgrade) {
      buttonColor = 'var(--prometheus-primary)'
    } else {
      buttonColor = 'var(--prometheus-warning)'
    }
  } else {
    buttonColor = translateOrbitingPlanetColorRaw(orbit)
  }

  const button = (
    <PromiseButtonV2 ignoreGameLogic
      size="sm"
      disabled={disabled}
      style={{ backgroundColor: buttonColor, borderColor: buttonColor }}
      onClick={() => (isPurchase ? onUpgradePlanet(planet, 'purchase') : onPickChoosablePlanet(planet))}
    >
      {!disabled && (<Icon name={isPurchase ? 'dollar sign' : 'rocket'} style={{ position: 'relative', top: '-3px' }} />)}
      {buttonText}
    </PromiseButtonV2>
  )

  // A readout, not an action: where the planet is in its cycle, or why it is
  // stopped. Hovering explains the clock.
  const cycleChip = (
    <span
      className={classNames('pg-planet-cycle', { 'is-locked': isWaitingForResources })}
      aria-label={isWaitingForResources
        ? `Waiting on resources. Mines pay in ${yieldInYears} years.`
        : disable_planet_tokens
          ? `${round(sublevels_current, 1)} of ${tile} years through this planet's cycle.`
          : `Upgrades when the dice roll ${tile}.`}
    >
      {disable_planet_tokens && isWaitingForResources
        ? `🔒 ⛏${yieldInYears ?? '?'}y`
        : (disable_planet_tokens ? `${round(sublevels_current, 1)}/${tile}` : `${tile}`)}
    </span>
  )

  // Its own control, because buying a faster tile has nothing to do with how
  // far through its cycle the planet happens to be.
  const tileButton = (
    <PromiseButtonV2 ignoreGameLogic
      size="sm"
      className="icon"
      style={{ backgroundColor: buttonColor, borderColor: buttonColor }}
      onClick={() => onUpgradePlanet(planet, 'tile').catch(e => dispatch(pushError({ message: e.message, type: 'error' })))}
    >
      <Icon name="angle double up" style={{ position: 'relative', top: '-2px' }} />
      {next_resource}
    </PromiseButtonV2>
  )

  return (
    <div className={classNames({ 'leveling-up': isLevelingUp, 'awaiting-upgrade': isWaitingForResources })} style={{ ...(owned && !fullscreen ? { width: '100%' } : {}), ...style }}>
      <Card variant="elevated" padding="none" style={{ ...((owned && !fullscreen) ? { width: '100%', borderColor: translateOrbitingPlanetColorRaw(orbit) } : { width: '260px', borderColor: translateOrbitingPlanetColorRaw(orbit) }) }}>
        <Box space="sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Popup
            position="bottom center"
            trigger={(
              <Typography bold style={{ color: translateOrbitingPlanetColorRaw(orbit) }} onClick={() => onFocusPlanet(planet)} variant="h5" retro>{planet.split(':')[1]}</Typography>
            )}
          >
            <Card padding="sm">
              <Typography>Show on map</Typography>
            </Card>
          </Popup>
          
          {owned ? (
            <Stack space="xs" direction="row" style={{ alignItems: 'center' }}>
              <PlanetCyclePopup
                trigger={cycleChip}
                disablePlanetTokens={!!disable_planet_tokens}
                // Only while genuinely holding: a mid-turn tile Enhance can
                // leave a stale counter behind for a turn, and the popup must
                // not claim a growing planet is waiting.
                holdingYears={isWaitingForResources ? upgrade_hold_turns : 0}
              />
              {nextTileUpgrade.available && (
                <TileUpgradesPopup planet={planet} trigger={tileButton} />
              )}
            </Stack>
          ) : (buttonText && button)}
        </Box>
        {isDeed && <PlanetDetailsDisplayDeed deed={deed as PlanetDeed} planet={planet} isLevelingUp={isLevelingUp} owned={owned || false} ownedPlanet={ownedPlanet} />}
        {isLoan && isPurchase && <PlanetDetailsDisplayLoan deed={deed as StarbaseLoan} planet={planet} />}
        {connections && (
          <>
            <Divider />
            {connections}
            <Divider />
          </>
        )}
      </Card>
    </div>
  )
}
